import { useState } from "react";
import { APP_URL } from "@/lib/app-config";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import AppleSignInButton from "@/components/AppleSignInButton";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, user, loading: authLoading } = useAuth();
  const { isAdmin, isProvider, isSalesperson, isPharmacy, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Where to send the user after login. ProtectedRoute stashes the original
  // path in location.state.from when it bounces an unauth user to /login.
  const fromPath = (location.state as { from?: string } | null)?.from;
  const isSafeReturn = (p?: string) =>
    !!p && p.startsWith("/") && !p.startsWith("//") && p !== "/login";

  if (authLoading || (user && roleLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-6">
          <Skeleton className="h-4 w-32" />
          <div className="space-y-2">
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-5 w-64" />
          </div>
          <Card className="border-border bg-card">
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
          <Skeleton className="h-4 w-56 mx-auto" />
        </div>
      </div>
    );
  }

  if (user) {
    // Honor a return path captured by ProtectedRoute when present.
    if (isSafeReturn(fromPath)) return <Navigate to={fromPath!} replace />;
    if (isAdmin) return <Navigate to="/admin" replace />;
    if (isProvider) return <Navigate to="/provider" replace />;
    if (isSalesperson) return <Navigate to="/sales" replace />;
    if (isPharmacy) return <Navigate to="/pharmacy" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const { error } = await signIn(email, password);
    setIsLoading(false);
    if (error) {
      toast({ title: "Login failed", description: error.message, variant: "destructive" });
    } else {
      // 1) If user was bounced from a protected page, send them back there.
      if (isSafeReturn(fromPath)) {
        navigate(fromPath!, { replace: true });
        return;
      }
      // 2) Otherwise route by role.
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", currentUser.id);
        const roleList = roles?.map((r: any) => r.role) || [];
        if (roleList.includes("admin")) {
          navigate("/admin");
          return;
        }
        if (roleList.includes("provider")) {
          navigate("/provider");
          return;
        }
        if (roleList.includes("salesperson")) {
          navigate("/sales");
          return;
        }
        if (roleList.includes("pharmacy")) {
          navigate("/pharmacy");
          return;
        }
      }
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Helmet>
        <title>Sign In | Elara Health Patient Portal</title>
        <meta name="description" content="Sign in to your Elara Health account to manage your peptide therapy, view prescriptions, and book consultations." />
        <link rel="canonical" href={`${APP_URL}/login`} />
        <meta property="og:title" content="Sign In | Elara Health Patient Portal" />
        <meta property="og:url" content={`${APP_URL}/login`} />
      </Helmet>
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={16} /> Back to home
        </Link>

        <div>
          <h1 className="font-display text-3xl text-foreground">Welcome back</h1>
          <p className="text-muted-foreground mt-1">Log in to your Elara Health account</p>
        </div>

        <Card className="border-border bg-card">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
                </div>
                <div className="relative">
                  <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Logging in..." : "Log In"}
              </Button>
              <GoogleSignInButton label="Sign in with Google" />
              <AppleSignInButton label="Sign in with Apple" />
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link to="/get-started" className="text-primary hover:underline font-medium">Get started</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
